// Permissões granulares REAIS por usuário (GPS Drive Backend) — aba
// PERMISSÕES em Editar Usuário. Substitui a antiga matriz por PAPEL
// (decisão 2026-08-26, revertida no mesmo dia: por USUÁRIO, não por
// papel — ver auditoria da etapa). "Grupo" (papel) continua existindo
// via gpsdrive-roles.service.ts, só para o select do topo — as
// permissões abaixo são sempre do usuário individual.
import { gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

// Mesma chave fechada usada no backend
// (src/user-permissions/permission-catalog.constants.ts) — nunca
// inventar uma categoria/ação nova só no frontend, os dois lados
// precisam concordar na mesma lista.
export type PermissionCategoryKey =
  | "administrador"
  | "devices"
  | "users"
  | "notifications"
  | "geofences"
  | "commands"
  | "reports"
  | "administracao"
  | "cliente"
  | "faturamento"
  | "documentos"
  | "contrato"
  | "financeiro"
  | "central_assistencia"
  | "gestao_frota"
  | "consultas"
  | "vendas"
  | "menus_app"
  | "vistoria"
  | "menus_web"
  | "agenda";

export interface PermissionCategoryDef {
  key: PermissionCategoryKey;
  label: string;
  icon: string;
  // Texto curto mostrado ao abrir a categoria na tela de permissões.
  hint?: string;
  actions: { key: string; label: string }[];
}

// Ordem fixa de exibição — mesma ordem do catálogo no backend.
export const PERMISSION_CATEGORIES: PermissionCategoryDef[] = [
  // Meta-permissão: acesso total ao sistema (2026-08-27, substitui os 4
  // toggles soltos que existiam no topo da aba — ver users.tsx). O backend
  // (requirePermission) bypassa qualquer outra checagem quando ela está
  // ligada.
  {
    key: "administrador",
    label: "Administrador",
    icon: "👑",
    actions: [],
  },
  {
    key: "devices",
    label: "Dispositivos",
    icon: "🖥",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Criar" },
      { key: "edit", label: "Editar" },
      { key: "delete", label: "Excluir" },
      { key: "anchor", label: "Ancorar" },
      { key: "lock", label: "Bloquear" },
      { key: "unlock", label: "Desbloquear" },
      { key: "send_command", label: "Enviar comandos" },
      { key: "tracker_info", label: "Ver dados do rastreador" },
    ],
  },
  {
    key: "users",
    label: "Usuários",
    icon: "👥",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Criar" },
      { key: "edit", label: "Editar" },
      { key: "delete", label: "Excluir" },
      { key: "impersonate", label: "Logar como" },
    ],
  },
  {
    key: "notifications",
    label: "Notificações",
    icon: "🔔",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Criar" },
      { key: "edit", label: "Editar" },
      { key: "delete", label: "Excluir" },
    ],
  },
  {
    key: "geofences",
    label: "Geocercas",
    icon: "📍",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Criar" },
      { key: "edit", label: "Editar" },
      { key: "delete", label: "Excluir" },
    ],
  },
  {
    key: "commands",
    label: "Comandos",
    icon: "⚡",
    actions: [{ key: "send_command", label: "Enviar comandos" }],
  },
  {
    key: "reports",
    label: "Relatórios",
    icon: "📊",
    actions: [
      { key: "routes", label: "Rotas" },
      { key: "stops", label: "Paradas" },
      { key: "trips", label: "Viagens" },
      { key: "events", label: "Eventos" },
      { key: "delete_history", label: "Apagar histórico" },
    ],
  },
  // Menu Administração (2026-08-27) — uma ação por seção da tela, não por
  // operação CRUD. Categoria comum: "administrador" acima já dá acesso a
  // tudo isso via bypass; esta existe para conceder acesso pontual a
  // alguma seção do Administração sem dar acesso total ao sistema.
  {
    key: "administracao",
    label: "Administração",
    icon: "🛠",
    actions: [
      { key: "identidade_visual", label: "Identidade Visual" },
      { key: "rastreadores_comandos", label: "Rastreadores & Comandos" },
      { key: "conta_integracoes", label: "Conta & Integrações" },
      { key: "comunicacao", label: "Comunicação" },
      { key: "notificacoes", label: "Notificações" },
      // Registrar Assistência (PWA, menu Perfil) + Configurar Assistência
      // (Administração → Comunicação) — a mesma ação cobre os dois lados.
      { key: "assistencia", label: "Assistência" },
      // Formulários públicos da empresa (pedido 2026-10-05).
      { key: "formularios", label: "Formulários" },
    ],
  },
  // 4 categorias novas (pedido 2026-09-03) — controlam as abas CLIENTE/
  // FATURAMENTO/DOCUMENTOS/CONTRATO de Adicionar/Editar Usuário, que antes
  // só exigiam "users: edit" genérico pra abrir o dialog inteiro, sem
  // controle fino próprio. "view" decide se a aba aparece; as demais
  // ações controlam os botões que mexem em dado dentro dela — ver
  // routes/users.tsx (canView*/canCreate*/canEdit*/canDelete*). Só
  // enforcement de FRONTEND por enquanto — mesma situação já existente de
  // Geocercas/Comandos/Relatórios (ver comentário em
  // permission.guard.ts no backend: essas rotas ainda vão direto
  // Frontend→GPS Drive Backend sem passar por requirePermission).
  {
    key: "cliente",
    label: "Cliente/Financeiro",
    icon: "🧾",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "edit", label: "Editar" },
    ],
  },
  {
    key: "faturamento",
    label: "Faturamento",
    icon: "💰",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Criar" },
      { key: "edit", label: "Editar" },
      { key: "delete", label: "Excluir" },
    ],
  },
  {
    key: "documentos",
    label: "Documentos",
    icon: "📎",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "create", label: "Enviar" },
      { key: "delete", label: "Excluir" },
    ],
  },
  {
    key: "contrato",
    label: "Contrato",
    icon: "📄",
    actions: [
      { key: "view", label: "Visualizar" },
      { key: "edit", label: "Editar" },
    ],
  },
  // Menu Financeiro (2026-09-28) — só aparece na tela de permissões de
  // funcionário (admin sempre vê tudo, cliente nunca). Mesmas chaves do
  // backend (permission-catalog.constants.ts); "view" é pré-requisito.
  {
    key: "financeiro",
    label: "Financeiro",
    icon: "💵",
    actions: [
      { key: "view", label: "Ver painel" },
      { key: "invoices", label: "Ver faturamento e clientes devendo" },
      { key: "expenses", label: "Ver despesas e entradas" },
      { key: "create", label: "Lançar despesas/entradas" },
      { key: "edit", label: "Editar lançamentos" },
      { key: "delete", label: "Excluir lançamentos" },
    ],
  },
  // Menu Assistência (2026-10-16) — mesma chave do backend. Só funcionário
  // (admin sempre tem tudo, cliente nunca).
  {
    key: "central_assistencia",
    label: "Assistência (guinchos)",
    icon: "🛻",
    actions: [
      { key: "view", label: "Ver painel, guinchos, mapa e solicitações" },
      { key: "manage", label: "Cadastrar/editar/excluir guinchos" },
      { key: "requests", label: "Atender solicitações (guincho, status, valor)" },
      { key: "cancel", label: "Cancelar solicitações" },
    ],
  },
  // Gestão de Frota (2026-10-01) — mesma chave do backend. Vale pra
  // funcionário e pra CLIENTE frotista (este só vê a própria frota).
  {
    key: "gestao_frota",
    label: "Gestão de Frota",
    icon: "🚛",
    actions: [
      { key: "view", label: "Ver painel e cadastros" },
      { key: "manage", label: "Cadastrar/editar motoristas, financeiro, manutenções e locações" },
    ],
  },
  // Consultas (2026-10-04) — CPF/antecedentes via API Full; mesma chave do backend.
  {
    key: "consultas",
    label: "Consultas",
    icon: "🔎",
    actions: [{ key: "view", label: "Fazer consultas (gasta créditos) e ver o histórico" }],
  },
  // Vendas → Pré-contratos (2026-10-05) — mesma chave do backend. Só
  // funcionário (vendedor); admin sempre tem, cliente nunca.
  {
    key: "vendas",
    label: "Vendas",
    icon: "🛒",
    actions: [
      { key: "view", label: "Ver pré-contratos" },
      { key: "edit", label: "Editar, criar cliente e gerar contrato" },
      { key: "delete", label: "Excluir pré-contratos" },
    ],
  },
  // Motorista (pedido 2026-10-08) — quais menus ele enxerga no app/web. Só
  // é aplicado pra accountType "motorista" (ver lib/account-type.ts);
  // cliente/equipe continuam vendo os menus como sempre.
  {
    key: "menus_app",
    label: "Menus do app",
    icon: "📱",
    actions: [
      { key: "mapa", label: "Mapa" },
      { key: "veiculos", label: "Veículos" },
      { key: "eventos", label: "Eventos" },
      { key: "historico", label: "Histórico de percurso" },
      { key: "compartilhar", label: "Compartilhar localização" },
    ],
  },
  // Vistoria do veículo (pedido 2026-10-08) — "create" = Perfil → Vistoria
  // no app; "view" = ver as vistorias de todos (equipe, tela Vistorias).
  {
    key: "vistoria",
    label: "Vistoria",
    icon: "📋",
    actions: [
      { key: "create", label: "Fazer vistoria do veículo" },
      { key: "view", label: "Ver vistorias de todos" },
    ],
  },
  // Menus do sistema (pedido 2026-10-09: "escolher quais menus o
  // funcionário pode ver — vendas não precisa de mapa, alertas..."). Só é
  // APLICADO pra funcionário (ver hooks/use-app-menu-access.ts). Os outros
  // menus (Usuários, Veículos, Financeiro, Gestão de Frota, Consultas,
  // Vendas, Administração) já aparecem conforme a própria categoria.
  {
    key: "menus_web",
    label: "Menus do sistema",
    icon: "🧭",
    hint: "Usuários, Veículos, Financeiro, Gestão de Frota, Consultas, Vendas, Agenda e Administração aparecem conforme as permissões de cada um.",
    actions: [
      { key: "inicio", label: "Início" },
      { key: "mapa", label: "Mapa" },
      { key: "geocercas", label: "Geocercas" },
      { key: "relatorios", label: "Relatórios" },
      { key: "camera", label: "Câmera" },
    ],
  },
  // Agenda (pedido 2026-10-09) — telas que o funcionário vê dentro da
  // Agenda e o que faz com agendamentos. Backend exige as ações de escrita
  // (requireFuncionarioPermission). Admin sempre tem tudo.
  {
    key: "agenda",
    label: "Agenda",
    icon: "📅",
    actions: [
      { key: "menu_dashboard", label: "Ver Dashboard" },
      { key: "menu_agendamentos", label: "Ver Agendamentos" },
      { key: "menu_chamados", label: "Ver Chamados" },
      { key: "menu_atendimentos", label: "Ver Atendimentos" },
      { key: "menu_parametro", label: "Parâmetro (técnicos, tópicos, tags, causas)" },
      { key: "menu_chips", label: "Gestão de Chips" },
      { key: "menu_estoque", label: "Estoque" },
      { key: "menu_configuracoes", label: "Configurações" },
      { key: "create", label: "Criar agendamento" },
      { key: "edit", label: "Editar/reabrir agendamento" },
      { key: "delete", label: "Excluir agendamento" },
    ],
  },
];

// Categorias mostradas na aba PERMISSÕES (e no painel Criar/Editar Grupo)
// quando o "Grupo do usuário" é Funcionário — pedido 2026-09-03: "quando
// o admin escolher o grupo funcionario tem que ter totalmente diferente a
// tela de permissões [...] tem que ser um acesso diferente de usuario
// cliente normal, não pode ser as mesmas permissões de usuario cliente".
// CORRIGIDO no mesmo dia (3ª rodada — a 2ª rodada tinha entendido errado
// "não precisa ter a aba de cliente/faturamento/documentos/contrato/
// dispositivo conectados, pois é funcionario" como "tirar essas categorias
// do que o Funcionário pode fazer" e removeu Cliente/Faturamento/Contrato
// daqui — ERRADO, causou "CADE AS PERMISSÕES DE FATURAMENTO CONTRATO ETC
// TA FALTANDO"). O pedido real era sobre a aba Dispositivos Conectados e
// as abas CLIENTE/FATURAMENTO/DOCUMENTOS/CONTRATO só quando o USUÁRIO
// SENDO EDITADO é ele próprio um Funcionário (empregado não tem perfil de
// cliente/financeiro/contrato próprio) — ver `accountType !== "funcionario"`
// em routes/users.tsx, não mais um "isFuncionarioEditor" baseado em quem
// está logado. Um Funcionário como EDITOR continua precisando de
// Cliente/Faturamento/Documentos/Contrato normalmente pra atender os
// clientes de verdade — por isso essas 4 voltam pro catálogo aqui, mais
// Administração (pedido novo: "o que ele pode ver dentro de
// administração"). Mesmo catálogo fechado (mesmas keys/ações) — nunca
// duplicar a definição da categoria aqui, sempre filtrar
// PERMISSION_CATEGORIES por esta lista.
export const FUNCIONARIO_CATEGORY_KEYS: PermissionCategoryKey[] = [
  // Menus visíveis (2026-10-09) — primeiro, é o que mais muda por função.
  "menus_web",
  "agenda",
  "administracao",
  "users",
  "devices",
  "commands",
  "geofences",
  // Notificações (2026-09-29): faltava — funcionário nunca podia receber
  // o feed de eventos/alertas nem criar regra de alerta.
  "notifications",
  "reports",
  "cliente",
  "faturamento",
  "documentos",
  "contrato",
  "financeiro",
  "central_assistencia",
  "gestao_frota",
  "consultas",
  "vendas",
  "vistoria",
];

// Tela de permissões do Motorista (pedido 2026-10-08: "poder marcar o que
// o motorista pode ver, menu de mapa etc, se pode bloquear desbloquear").
// Só o que faz sentido pra quem dirige — e, dentro de Dispositivos, só as
// ações de uso (nada de criar/editar/excluir cadastro). As demais ações
// continuam no estado salvo (o backend exige o catálogo inteiro), só não
// aparecem na tela.
const MOTORISTA_ACTIONS: Partial<Record<PermissionCategoryKey, string[]>> = {
  menus_app: ["mapa", "veiculos", "eventos", "historico", "compartilhar"],
  devices: ["view", "lock", "unlock", "anchor"],
  commands: ["send_command"],
  notifications: ["view"],
  vistoria: ["create"],
};
export const MOTORISTA_PERMISSION_CATEGORIES: PermissionCategoryDef[] = PERMISSION_CATEGORIES.filter(
  (c) => c.key in MOTORISTA_ACTIONS,
).map((c) => ({
  ...c,
  actions: c.actions.filter((a) => MOTORISTA_ACTIONS[c.key]!.includes(a.key)),
}));
// Ponto de partida ao escolher "Motorista" num usuário novo: vê mapa,
// veículos, eventos e histórico e faz vistoria; bloquear/desbloquear e
// comandos ficam desligados até o admin marcar.
export const MOTORISTA_DEFAULT_ON: Partial<Record<PermissionCategoryKey, string[]>> = {
  menus_app: ["mapa", "veiculos", "eventos", "historico"],
  devices: ["view"],
  notifications: ["view"],
  vistoria: ["create"],
};

// Ponto de partida ao escolher "Funcionário" num usuário novo: todos os
// menus e a Agenda inteira ligados (mesmo acesso que funcionário tinha antes
// destas categorias existirem) — o admin desliga o que aquela função não usa.
export const FUNCIONARIO_DEFAULT_ON: Partial<Record<PermissionCategoryKey, string[]>> = {
  menus_web: ["inicio", "mapa", "geocercas", "relatorios", "camera"],
  agenda: [
    "menu_dashboard",
    "menu_agendamentos",
    "menu_chamados",
    "menu_atendimentos",
    "menu_parametro",
    "menu_chips",
    "menu_estoque",
    "menu_configuracoes",
    "create",
    "edit",
    "delete",
  ],
};

export interface PermissionActionState {
  action: string;
  enabled: boolean;
}

export interface PermissionCategoryState {
  category: PermissionCategoryKey;
  enabled: boolean;
  actions: PermissionActionState[];
}

export const gpsDriveUserPermissionsService = {
  get: (userId: string) =>
    gpsDriveGet<PermissionCategoryState[]>(`/api/v1/users/${userId}/permissions`),
  // Permissões do PRÓPRIO usuário logado — self-scoped, qualquer usuário
  // autenticado pode chamar (sem exigir role Administrator, diferente de
  // `get` acima). Usado para decidir o que mostrar (ex.: item
  // "Administração" na sidebar, ver use-permission.ts), nunca para editar
  // permissão de outra pessoa.
  getMine: () => gpsDriveGet<PermissionCategoryState[]>("/api/v1/users/me/permissions"),
  // Substitui a matriz inteira do usuário de uma vez — o backend exige
  // todas as categorias/ações do catálogo fechado, nunca um subconjunto.
  replace: (userId: string, rows: PermissionCategoryState[]) =>
    gpsDrivePut<PermissionCategoryState[]>(`/api/v1/users/${userId}/permissions`, rows),
  // Ação explícita "Restaurar permissões padrão do grupo" — nunca disparada
  // automaticamente ao trocar o Grupo do usuário.
  restoreDefaults: (userId: string) =>
    gpsDrivePost<PermissionCategoryState[]>(`/api/v1/users/${userId}/permissions/restore-defaults`),
};
